import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import cn from 'classnames'
import { InfoOutlinedIcon } from 'assets/icons'
import ListWrapper from '../ListWrapper'
import useContent from 'hooks/api/useContent'
import useCollection from 'hooks/api/useCollection'
import { optionsParser } from 'helpers'
import { filterDuplicates } from 'helpers/propertiesParser'
import { useAppStore, useCastingStore, useUserStore } from 'store'
import { DROPDOWN_OPTIONS } from 'types/enums'
import { Content } from 'types/app'
import { PATHS } from 'types/enums'
import s from './index.module.scss'
import Tooltip from 'components/Tooltip'
import { useShallow } from 'zustand/react/shallow'
import useCastingOverviewFilter from 'hooks/useCastingOverviewFilter'
import { useSwipeable } from 'react-swipeable'

const Roles = (): JSX.Element => {
	const { productionId, roleId } = useParams()
	const navigate = useNavigate()
	const [search, setSearch] = useState('')
	const { filter, setFilter } = useCastingOverviewFilter('roles')
	const [collapsed, setCollapsed] = useCastingStore(
		useShallow((state) => [state.collapsed.roles, state.setCollapsed]),
	)

	const { content, isLoading: loading } = useContent(productionId)
	const { collection = [], isLoading } = useCollection(content, true)

	const tabs = [
		{
			label: optionsParser(DROPDOWN_OPTIONS.ROLE_STATUS)?.[0]?.label,
			value: optionsParser(DROPDOWN_OPTIONS.ROLE_STATUS)?.[0]?.value,
		},
		{
			label: optionsParser(DROPDOWN_OPTIONS.ROLE_STATUS)?.[1]?.label,
			value: optionsParser(DROPDOWN_OPTIONS.ROLE_STATUS)?.[1]?.value,
		},
		{
			label: optionsParser(DROPDOWN_OPTIONS.ROLE_STATUS)?.[2]?.label,
			value: optionsParser(DROPDOWN_OPTIONS.ROLE_STATUS)?.[2]?.value,
		},
	]

	const onClick = (content: Content): void => {
		const { setCastingLoading } = useCastingStore.getState()
		setCastingLoading(true)
		const { user } = useUserStore.getState()
		navigate(
			`/${user?.CustomerRole?.toLowerCase()}/${PATHS.CASTING}/${productionId}/${content.ContentId
			}` + window.location.hash,
		)
		setTimeout(() => {
			setCastingLoading(false)
		}, 500)
	}

	const list = useMemo(
		() =>
			filterDuplicates<Content>(collection || [], 'ContentId')
				?.filter((content) =>
					filter
						? content?.ContentRoleType?.toLowerCase() === filter?.toLowerCase()
						: content,
				)
				?.filter((content) =>
					content?.['Globalizationen-USContentTitle']
						?.toLowerCase()
						.includes(search.toLowerCase()),
				) as unknown as Content[],
		[search, collection, filter],
	)
	const tabValues = tabs.map(tab => tab.value)
	const currentIndex = tabValues.findIndex(val => val === filter)

	const handlers = useSwipeable({
		onSwipedLeft: () => {
			const nextIndex = (currentIndex + 1) % tabValues.length
			setFilter(tabValues[nextIndex])
		},
		onSwipedRight: () => {
			const prevIndex = (currentIndex - 1 + tabValues.length) % tabValues.length
			setFilter(tabValues[prevIndex])
		},
		delta: 50,
		trackTouch: true,
		trackMouse: false,
	})

	return (
		<div
			{...handlers}
		>
			<ListWrapper
				type="roles"
				tabs={tabs}
				setSearch={setSearch}
				filter={filter || ''}
				setFilter={setFilter}
				collapsed={collapsed}
				setCollapsed={(col) => setCollapsed({ roles: col })}
				style={{
					backgroundColor: 'color-mix(in srgb, var(--mono000) 40%, transparent)',
				}}>
				<>
					{!loading && !isLoading
						? list?.map((content) => (
							<div
								className={cn(s.role, {
									[s.active]: roleId === content.ContentId,
								})}
								key={content?.ContentId}>
								<Tooltip
									title={
										collapsed ? content?.['Globalizationen-USContentTitle'] : ''
									}>
									<div
										className={s.avatar}
										onClick={() => onClick(content)}
										style={{
											backgroundColor: content?.ContentColor,
										}}>
										<div className="cardShadowBackground" />
										{/* <img
										src={content?.ContentSquareImageUrl || '/placeholder.png'}
										alt={content?.ContentTitle}
										/> */}
									</div>
								</Tooltip>
								<div className={s.info} onClick={() => onClick(content)}>
									<span className="body1-m textEllipsis">
										{content?.['Globalizationen-USContentTitle']}
									</span>
									<span
										className="body2-r"
										style={{ color: 'var(--mono200)' }}></span>
								</div>

								<div
									onClick={() => {
										const { setRoleInfoSidebar } = useAppStore.getState()
										setRoleInfoSidebar(content.ContentId)
									}}
									className={s.infoButton}>
									<InfoOutlinedIcon />
								</div>
							</div>
						))
						: null}
				</>
			</ListWrapper>
		</div>
	)
}

export default Roles
